import React, { useState, forwardRef } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { cn } from "../lib/utils";

const QuickLook = forwardRef(({ product }, ref) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Combine main image with additional images
  const allImages = [product.image, ...(product.additionalImages || [])];

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1,
    );
  };

  const previousImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1,
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          size="icon"
          className="aspect-square"
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="!duration-75 !animate-none !data-[state=open]:animate-none !data-[state=closed]:animate-none max-w-4xl border-none bg-transparent p-0 sm:rounded-none">
        <DialogTitle className="sr-only">
          {product.name} - Product Image Gallery
        </DialogTitle>
        <div className="relative">
          <img
            src={allImages[currentImageIndex]}
            alt={`${product.name} enlarged view`}
            className="w-full h-auto object-contain max-h-[80vh] rounded-lg"
          />
          {allImages.length > 1 && (
            <>
              <Button
                variant="outline"
                size="icon"
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full"
                onClick={previousImage}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full"
                onClick={nextImage}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex justify-center gap-2">
                {allImages.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-colors",
                      currentImageIndex === index
                        ? "bg-blue-600"
                        : "bg-white/80 hover:bg-white",
                    )}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

QuickLook.displayName = "QuickLook";

QuickLook.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    additionalImages: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default QuickLook;
